import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { io, Socket } from "socket.io-client";
import {
  inboxKeys,
  messageCacheAcceptsChannel,
  upsertMessageIntoPages,
  type PaginatedMessages
} from "./hooks";
import { inboxApi } from "./api";
import { crmKeys, CrmContact } from "./crm";
import { markAutoSent, applyAutoSentFlags } from "./autoSentRegistry";

const WS_URL = import.meta.env.VITE_APP_INBOX_WS_URL;

interface InfiniteCache<TPage> {
  pages: TPage[];
  pageParams: unknown[];
}

type CachedConversation = { id: string; unread_count?: number };
type ConversationsPage = { conversations: CachedConversation[] };
type ConversationsCache = InfiniteCache<ConversationsPage>;

// Immediately patch an already-cached copy of an auto-sent message. Belt-and-
// suspenders alongside the shared autoSentRegistry (which the messages query's
// `select` also consults, so the label survives any later refetch/pagination).
const markMessageAutoSentInCache = (
  oldData: PaginatedMessages | undefined,
  messageId: string
): PaginatedMessages | undefined => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      messages: page.messages.map((msg) => {
        const message = msg as { id: string };
        return message.id === messageId
          ? { ...message, ai_auto_sent: true, from_ai_suggestion: true }
          : msg;
      })
    }))
  };
};

const updateConversationUnreadInCache = (
  oldData: ConversationsCache | undefined,
  conversationId: string,
  unreadCount: number | undefined
) => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page) => ({
      ...page,
      conversations: page.conversations.map((conv) =>
        conv.id === conversationId
          ? { ...conv, unread_count: unreadCount }
          : conv
      )
    }))
  };
};

interface UnreadCountUpdatePayload {
  user_id: number;
  user_type: string;
  total_unread: number;
  conversation_id?: string;
  conversation_unread?: number;
}

interface NewMessagePayload {
  conversation_id: string;
  message_id: string;
}

interface MessageStatusPayload {
  message_id?: string;
  status?: string;
  [key: string]: unknown;
}

interface ConversationUpdatePayload {
  conversation_id?: string;
  [key: string]: unknown;
}

interface ContactUpdatedPayload {
  contact_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

interface CrmContactListCache {
  items: CrmContact[];
  [key: string]: unknown;
}

const patchContactInCache = (
  oldData: CrmContactListCache | undefined,
  payload: ContactUpdatedPayload
) => {
  if (!oldData?.items) return oldData;
  let changed = false;
  const items = oldData.items.map((c) => {
    if (c.id !== payload.contact_id) return c;
    changed = true;
    return {
      ...c,
      first_name: payload.first_name ?? "",
      last_name: payload.last_name ?? ""
    };
  });
  return changed ? { ...oldData, items } : oldData;
};

interface UseInboxWebSocketProps {
  accessToken: string | undefined;
  enabled?: boolean;
  onNewMessage?: (data: NewMessagePayload) => void;
  clearUnreadOnOpenConversation?: boolean;
  onMessageStatus?: (data: MessageStatusPayload) => void;
  onConversationUpdate?: (data: ConversationUpdatePayload) => void;
  onUnreadCountUpdate?: (data: UnreadCountUpdatePayload) => void;
}

export const useInboxWebSocket = ({
  accessToken,
  enabled = true,
  clearUnreadOnOpenConversation = true,
  onNewMessage,
  onMessageStatus,
  onConversationUpdate,
  onUnreadCountUpdate
}: UseInboxWebSocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  const clearUnreadRef = useRef(clearUnreadOnOpenConversation);
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageStatusRef = useRef(onMessageStatus);
  const onConversationUpdateRef = useRef(onConversationUpdate);
  const onUnreadCountUpdateRef = useRef(onUnreadCountUpdate);

  onNewMessageRef.current = onNewMessage;

  useEffect(() => {
    clearUnreadRef.current = clearUnreadOnOpenConversation;
  }, [clearUnreadOnOpenConversation]);
  onMessageStatusRef.current = onMessageStatus;
  onConversationUpdateRef.current = onConversationUpdate;
  onUnreadCountUpdateRef.current = onUnreadCountUpdate;

  // Main effect: create socket connection only when token/enabled changes
  useEffect(() => {
    if (!accessToken || !enabled) return;

    const socket = io(`${WS_URL}/portal/inbox`, {
      auth: { token: accessToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: Infinity
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      if (currentConversationIdRef.current)
        socket.emit("join_conversation", {
          conversation_id: currentConversationIdRef.current
        });
    });

    socket.on("disconnect", (reason) => {
      setIsConnected(false);
      console.warn(`[InboxWebSocket] Disconnected: ${reason}`);
    });

    socket.on("connect_error", (error) => {
      console.error(`[InboxWebSocket] Connection error: ${error.message}`);
      setIsConnected(false);
    });

    socket.on("new_message", async (data) => {
      const conversationId = data.conversation_id;
      const messageId = data.message_id;
      const isCurrentConversation =
        conversationId === currentConversationIdRef.current;

      await queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });

      queryClient.invalidateQueries({
        queryKey: inboxKeys.unreadConversationsCount(),
        refetchType: "active"
      });

      if (isCurrentConversation && clearUnreadRef.current)
        queryClient.setQueriesData<ConversationsCache>(
          { queryKey: [...inboxKeys.conversations(), "LIST"] },
          (oldData) =>
            updateConversationUnreadInCache(oldData, conversationId, 0)
        );

      // A STOP/START keyword arrives as a normal inbound message, so refresh
      // the conversation's SMS opt-out state to re-gate the composer live.
      if (conversationId)
        queryClient.invalidateQueries({
          queryKey: inboxKeys.smsOptOuts(conversationId),
          refetchType: "active"
        });

      if (conversationId && messageId) {
        const messagesPrefix = [
          ...inboxKeys.conversation(conversationId),
          "MESSAGES"
        ];

        // Fetch the full message from the API (includes attachments, HTML, etc.)
        // This handles both new messages and refresh signals (e.g. after
        // attachment uploads complete) — upserts into the cache.
        try {
          const { message } = await inboxApi.getMessage(
            conversationId,
            messageId
          );

          // If the authoritative auto-sent event already named this message,
          // keep the AI-auto-sent label even when this (possibly stale) fetch
          // read the flags as false. The messages query's `select` re-applies
          // this on every read too, so a later refetch can't downgrade it.
          const stampedMessage = applyAutoSentFlags(message);

          // The prefix match stops at "MESSAGES", so it also selects
          // channel-filtered caches (the message pop-up's { channel: "chat" }).
          // Those must only ever receive their own channel — otherwise an
          // inbound email shows up as a chat bubble until something refetches.
          queryClient.setQueriesData<PaginatedMessages>(
            {
              queryKey: messagesPrefix,
              predicate: (query) =>
                messageCacheAcceptsChannel(query.queryKey, message.channel)
            },
            (oldData) => upsertMessageIntoPages(oldData, stampedMessage)
          );
        } catch (fetchError: unknown) {
          console.warn("[InboxWebSocket] Failed to fetch message:", fetchError);
          // Fallback: if single fetch fails, refetch the full message list
          queryClient.invalidateQueries({
            queryKey: messagesPrefix,
            refetchType: "active"
          });
        }
      }

      onNewMessageRef.current?.(data);
    });

    socket.on("message_status_update", (data) => {
      if (data.message_id)
        queryClient.invalidateQueries({
          queryKey: inboxKeys.all,
          refetchType: "active"
        });

      onMessageStatusRef.current?.(data);
    });

    socket.on("conversation_update", (data) => {
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });
      // Refetch an open conversation's detail too: the escalation flag/banner
      // (CRM-1438) lands via this event while the thread is on screen.
      if (data.conversation_id)
        queryClient.invalidateQueries({
          queryKey: inboxKeys.conversation(data.conversation_id),
          exact: true,
          refetchType: "active"
        });

      onConversationUpdateRef.current?.(data);
    });

    // AI-216: a suggested reply was generated or failed for a conversation.
    // The draft lives on the anchor message (read-API includes suggested_reply),
    // so refetch that conversation's messages to surface/update the card.
    // Also refetch the conversation detail: a suggestion event proves the
    // backend gate passed, so a stale ai_suggestions_available=false (e.g.
    // the leasing agent was just reactivated) must not keep the switch
    // showing disabled while cards are arriving.
    const handleSuggestionEvent = (data: { conversation_id?: string }) => {
      if (!data?.conversation_id) return;
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversation(data.conversation_id), "MESSAGES"],
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: inboxKeys.conversation(data.conversation_id),
        exact: true,
        refetchType: "active"
      });
    };

    socket.on("suggestion_pending", handleSuggestionEvent);
    socket.on("suggestion_ready", handleSuggestionEvent);
    socket.on("suggestion_failed", handleSuggestionEvent);
    // Auto-response: the draft was auto-sent. This event is authoritative and
    // names the outbound message, so record its id in the shared registry
    // (the messages query's `select` re-stamps the flag on every read, so no
    // refetch/pagination can downgrade the label), patch any already-cached
    // copy immediately, then refetch to clear the pending card.
    socket.on(
      "suggestion_auto_sent",
      (data: { conversation_id?: string; outbound_message_id?: string }) => {
        if (data?.outbound_message_id) {
          markAutoSent(data.outbound_message_id);
          if (data.conversation_id)
            queryClient.setQueriesData<PaginatedMessages>(
              {
                queryKey: [
                  ...inboxKeys.conversation(data.conversation_id),
                  "MESSAGES"
                ]
              },
              (oldData) =>
                markMessageAutoSentInCache(oldData, data.outbound_message_id!)
            );
        }
        handleSuggestionEvent(data);
      }
    );
    socket.on("contact_updated", (data: ContactUpdatedPayload) => {
      // Optimistic patch for contacts already in the cached list so the
      // sidebar name swaps instantly. Falls back to a refetch below when
      // the contact is brand new and not in the cached page.
      queryClient.setQueriesData<CrmContactListCache>(
        { queryKey: crmKeys.contacts() },
        (oldData) => patchContactInCache(oldData, data)
      );
      queryClient.invalidateQueries({
        queryKey: crmKeys.contacts(),
        refetchType: "active"
      });
      queryClient.invalidateQueries({
        queryKey: [...inboxKeys.conversations(), "LIST"],
        refetchType: "active"
      });
    });

    socket.on("unread_count_update", (data: UnreadCountUpdatePayload) => {
      queryClient.invalidateQueries({
        queryKey: inboxKeys.unreadConversationsCount(),
        refetchType: "active"
      });

      if (
        data.conversation_id !== undefined &&
        data.conversation_id !== currentConversationIdRef.current
      )
        queryClient.setQueriesData<ConversationsCache>(
          { queryKey: [...inboxKeys.conversations(), "LIST"] },
          (oldData) =>
            updateConversationUnreadInCache(
              oldData,
              data.conversation_id!,
              data.conversation_unread
            )
        );

      onUnreadCountUpdateRef.current?.(data);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [accessToken, enabled, queryClient]);

  const joinConversation = useCallback((conversationId: string) => {
    const socket = socketRef.current;
    if (!socket) return;

    currentConversationIdRef.current = conversationId;

    if (socket.connected)
      socket.emit("join_conversation", { conversation_id: conversationId });
    else
      socket.once("connect", () => {
        socket.emit("join_conversation", { conversation_id: conversationId });
      });
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    const socket = socketRef.current;
    if (socket?.connected)
      socket.emit("leave_conversation", { conversation_id: conversationId });

    if (currentConversationIdRef.current === conversationId)
      currentConversationIdRef.current = null;
  }, []);

  return {
    isConnected,
    joinConversation,
    leaveConversation
  };
};
